import * as React from 'react';
import { Select, Spin, Tabs, message } from 'antd';
import { CheckCircleOutlined, PhoneOutlined, SyncOutlined } from '@ant-design/icons';
import { TableColumn } from 'react-data-table-component';

import { Badge } from 'components/badge/badge';
import { Button, buttonType } from 'components/button/button';
import { Card } from 'components/card/card';
import { CopyablePhone } from 'components/copyable-phone/copyable-phone';
import { Input } from 'components/input/input';
import { Page } from 'components/page/page';
import { Table } from 'components/table/table';
import { UserLink } from 'components/user-link/user-link';
import {
    CallTask,
    MarketingActivity,
    MarketingActivityStatus,
    MarketingActivityType,
    PaginationList,
    campaignSmsBadges,
} from 'core/domain';
import { getCallTasks, startCallTask } from 'core/repositories/marketing';
import { ActivitiesPanel } from '../activities/activities-panel';
import { UpdateActivityModal } from '../activities/activity-modals';
import { faNum, formatDate, timeAgo, toQuery } from '../marketing-utils';
import '../marketing.scss';

const { useEffect, useState } = React;

type TabKey = 'todo' | 'doing' | 'done';

const hourOptions = [24, 48, 72, 168].map((hours) => ({
    value: hours,
    label: hours < 168 ? `${faNum(hours)} ساعت گذشته` : 'یک هفته‌ی گذشته',
}));

// LevelUniqueName بک‌اند؛ ۰ یعنی بدون فیلتر
const levelOptions = [
    { value: 0, label: 'همه‌ی سطح‌ها' },
    { value: 1, label: 'پایه (احراز نکرده)' },
    { value: 2, label: 'سطح نقره‌ای (احراز کرده، خرید نکرده)' },
];

interface TodoProps {
    /** بعد از برداشتن تسک، تب‌های دیگر باید تازه شوند. */
    onTaskStarted: () => void;
}

/** کاربرانی که در بازه ثبت‌نام کرده‌اند، احراز نکرده‌اند و هنوز کسی با آن‌ها تماس نگرفته. */
const TodoTasks: React.FC<TodoProps> = ({ onTaskStarted }) => {
    const [hours, setHours] = useState(48);
    const [level, setLevel] = useState(0);
    const [keyword, setKeyword] = useState('');
    const [query, setQuery] = useState('');
    const [data, setData] = useState<PaginationList<CallTask>>();
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [startingUserId, setStartingUserId] = useState<string>();
    const [resultActivity, setResultActivity] =
        useState<Pick<MarketingActivity, 'id' | 'type' | 'status' | 'description'>>();

    const load = (nextHours = hours, nextKeyword = keyword, page = 1, nextLevel = level) => {
        const nextQuery = toQuery({ hours: nextHours, keyword: nextKeyword.trim(), level: nextLevel || undefined });
        setQuery(nextQuery);
        getCallTasks(setLoading, setData, page, pageSize, nextQuery);
    };

    const reloadCurrentPage = () => getCallTasks(setLoading, setData, data?.pagination?.pageIndex || 1, pageSize, query);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const start = async (task: CallTask) => {
        setStartingUserId(task.userId);
        const result = await startCallTask(task.userId);
        setStartingUserId(undefined);
        if (!result) {
            // احتمالا کارشناس دیگری زودتر برداشته؛ لیست تازه شود.
            reloadCurrentPage();
            return;
        }
        message.success('تسک به شما سپرده شد و به «در حال انجام» رفت.');
        reloadCurrentPage();
        onTaskStarted();
        // بلافاصله فرم ثبت نتیجه باز می‌شود؛ اگر بسته شود، تسک در تب «در حال انجام» می‌ماند.
        setResultActivity({
            id: result.activityId,
            type: MarketingActivityType.Call,
            status: MarketingActivityStatus.Doing,
        });
    };

    const columns: TableColumn<CallTask>[] = [
        {
            name: 'کاربر',
            id: 'user',
            selector: (row) => row.phoneNumber ?? '',
            cell: (row) => (
                <div className="mk-user-cell">
                    <UserLink userId={row.userId}>{row.fullName?.trim() || 'بدون نام'}</UserLink>
                    <CopyablePhone phoneNumber={row.phoneNumber} />
                </div>
            ),
            minWidth: '180px',
        },
        {
            name: 'سطح',
            id: 'level',
            selector: (row) => row.level ?? 0,
            cell: (row) =>
                row.level === 2 ? (
                    <Badge text="احراز شده، بدون خرید" type="info" />
                ) : (
                    <Badge text="احراز نکرده" type="waiting" />
                ),
            minWidth: '150px',
        },
        {
            name: 'ثبت‌نام',
            id: 'registeredOn',
            selector: (row) => row.registeredOn,
            cell: (row) => (
                <div className="mk-user-cell">
                    <span>{timeAgo(row.registeredOn)}</span>
                    <small>{formatDate(row.registeredOn)}</small>
                </div>
            ),
            minWidth: '150px',
        },
        {
            name: 'پیامک کمپین بازگشت',
            id: 'campaignSmsStatus',
            selector: (row) => row.campaignSmsStatus ?? 0,
            cell: (row) => {
                const badge = row.campaignSmsStatus ? campaignSmsBadges[row.campaignSmsStatus] : undefined;
                return badge ? <Badge text={badge.text} type={badge.type} /> : <span className="mk-muted">هنوز نه</span>;
            },
            minWidth: '140px',
        },
        {
            name: '',
            id: 'action',
            width: '140px',
            cell: (row) => (
                <div className="mk-start-call">
                    <Button
                        type={buttonType.info}
                        text="شروع تماس"
                        icon={<PhoneOutlined />}
                        loading={startingUserId === row.userId}
                        disabled={!!startingUserId}
                        onClick={() => start(row)}
                    />
                </div>
            ),
        },
    ];

    return (
        <div className="mk-panel">
            <UpdateActivityModal
                title="ثبت نتیجه‌ی تماس"
                activity={resultActivity}
                setActivity={setResultActivity}
                onSaved={onTaskStarted}
            />
            <div className="mk-panel__toolbar">
                <p>
                    کاربرانی که در بازه‌ی انتخابی ثبت‌نام کرده‌اند و یا احراز هویت نکرده‌اند، یا احراز کرده‌اند ولی هنوز
                    خریدی ندارند. با «شروع تماس» تسک به شما سپرده می‌شود و از این لیست خارج می‌شود.
                </p>
            </div>
            <div className="mk-panel__filters">
                <Input
                    value={keyword}
                    placeholder="شماره موبایل یا نام کاربر"
                    onPressEnter={() => load()}
                    onChange={(e) => setKeyword(e.target.value)}
                />
                <Select
                    value={hours}
                    options={hourOptions}
                    onChange={(value) => {
                        setHours(value);
                        load(value);
                    }}
                />
                <Select
                    value={level}
                    options={levelOptions}
                    onChange={(value) => {
                        setLevel(value);
                        load(hours, keyword, 1, value);
                    }}
                />
                <div className="mk-filter-buttons">
                    <Button type={buttonType.success} text="جستجو" disabled={loading} onClick={() => load()} />
                    <Button
                        type={buttonType.secondary}
                        text="بروزرسانی"
                        icon={<SyncOutlined />}
                        disabled={loading}
                        onClick={reloadCurrentPage}
                    />
                </div>
            </div>
            <Spin spinning={loading}>
                {data?.items?.length ? (
                    <Table
                        blur={loading}
                        data={data.items}
                        column={columns}
                        pagination={data.pagination}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        setloader={setLoading}
                        setdata={setData}
                        getdata={getCallTasks}
                        filters={query}
                    />
                ) : (
                    <p className="mk-empty">
                        {loading ? 'در حال بارگذاری...' : 'تسکی برای تماس نمانده است.'}
                    </p>
                )}
            </Spin>
            {!!data?.pagination?.totalCount && (
                <p className="mk-muted">{faNum(data.pagination.totalCount)} کاربر در انتظار تماس</p>
            )}
        </div>
    );
};

export const CallTasks: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabKey>('todo');
    // تب‌های در حال انجام / انجام شده با هر تغییر تسک دوباره بارگذاری می‌شوند.
    const [reloadKey, setReloadKey] = useState(0);
    const bump = () => setReloadKey((key) => key + 1);

    return (
        <Page className="pmx-marketing" title="تسک‌های تماس">
            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key as TabKey)}
                    items={[
                        {
                            key: 'todo',
                            label: (
                                <span>
                                    <PhoneOutlined /> در انتظار تماس
                                </span>
                            ),
                            forceRender: true,
                            children: <TodoTasks onTaskStarted={bump} />,
                        },
                        {
                            key: 'doing',
                            label: (
                                <span>
                                    <SyncOutlined /> در حال انجام
                                </span>
                            ),
                            children: activeTab === 'doing' && (
                                <ActivitiesPanel
                                    fixedType={MarketingActivityType.Call}
                                    fixedStatus={MarketingActivityStatus.Doing}
                                    reloadKey={reloadKey}
                                    hint="تماس‌هایی که برداشته شده‌اند ولی نتیجه‌شان ثبت نشده. با ویرایش، وضعیت را «انجام شده» کنید."
                                />
                            ),
                        },
                        {
                            key: 'done',
                            label: (
                                <span>
                                    <CheckCircleOutlined /> انجام شده
                                </span>
                            ),
                            children: activeTab === 'done' && (
                                <ActivitiesPanel
                                    fixedType={MarketingActivityType.Call}
                                    fixedStatus={MarketingActivityStatus.Done}
                                    reloadKey={reloadKey}
                                />
                            ),
                        },
                    ]}
                />
            </Card>
        </Page>
    );
};
